import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Camera, 
  Upload, 
  Copy, 
  ExternalLink, 
  Check, 
  X, 
  ArrowLeft, 
  Volume2, 
  VolumeX, 
  QrCode, 
  History, 
  RefreshCw, 
  AlertCircle, 
  Play, 
  Square,
  Lock,
  Info
} from 'lucide-react';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { AdUnit } from './AdUnit';

export const QRScanner = () => {
  const [activeTab, setActiveTab] = useState('camera'); // camera, file
  const [scannedText, setScannedText] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [scannedHistory, setScannedHistory] = useState([]);
  
  // Camera scanning states
  const [isScanning, setIsScanning] = useState(false);
  const [camerasList, setCamerasList] = useState([]);
  const [activeCameraId, setActiveCameraId] = useState('');
  const [cameraError, setCameraError] = useState(null);
  const [beepEnabled, setBeepEnabled] = useState(true);
  const [isCopied, setIsCopied] = useState(false);

  // File scanning states
  const [fileError, setFileError] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');

  const html5QrCodeRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load scan history from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('toolmetrico_scan_history');
    if (saved) {
      try {
        setScannedHistory(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse scan history", e);
      }
    }
  }, []);

  // Sync scan history to localStorage
  const saveHistory = (newList) => {
    setScannedHistory(newList);
    localStorage.setItem('toolmetrico_scan_history', JSON.stringify(newList));
  };

  const clearHistory = () => {
    saveHistory([]);
  };

  // Synthesize short success beep using standard web audio API
  const triggerSuccessBeep = () => {
    if (!beepEnabled) return;
    try {
      const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtxClass) return;
      const audioCtx = new AudioCtxClass();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime); // A5 note
      gainNode.gain.setValueAtTime(0.12, audioCtx.currentTime);
      
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.15); // Play for 150ms
    } catch (err) {
      console.warn("Failed to play audio synthesis beep:", err);
    }
  };

  // Check and list available cameras
  const initCameras = () => {
    if (!window.Html5Qrcode) {
      setCameraError("Scanner library is still loading. Please wait a moment.");
      return;
    }

    setCameraError(null);
    window.Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length > 0) {
          setCamerasList(devices);
          // Auto select first camera
          if (!activeCameraId) {
            setActiveCameraId(devices[0].id);
          }
        } else {
          setCameraError("No video capture devices (cameras) found. Please connect one or switch to Image File Upload tab.");
        }
      })
      .catch((err) => {
        console.error("getCameras failed", err);
        setCameraError("Camera permissions denied or device blocked. Please check your browser permission bar!");
      });
  };

  // Trigger camera query on tab mount
  useEffect(() => {
    if (activeTab === 'camera') {
      initCameras();
    } else {
      stopCameraScanning();
    }
    
    return () => {
      stopCameraScanning();
    };
  }, [activeTab]);

  // Start the live video camera scanner
  const startCameraScanning = () => {
    if (!activeCameraId) {
      setCameraError("Please select a camera device first.");
      return;
    }

    if (!window.Html5Qrcode) return;

    setCameraError(null);
    setScannedText(null);

    // Stop current scanning if any
    stopCameraScanning().then(() => {
      try {
        const html5QrCode = new window.Html5Qrcode("scanner-video-pane");
        html5QrCodeRef.current = html5QrCode;

        const config = {
          fps: 12,
          qrbox: (width, height) => {
            const minSize = Math.min(width, height);
            const boxSize = Math.floor(minSize * 0.65);
            return { width: boxSize, height: boxSize };
          }
        };

        html5QrCode.start(
          activeCameraId,
          config,
          (decodedText) => {
            // Success handler
            triggerSuccessBeep();
            setScannedText(decodedText);
            setShowResultModal(true);
            
            // Save to history
            const newItem = {
              text: decodedText,
              timestamp: new Date().toLocaleString(),
              id: Date.now()
            };
            
            // Prepend new scan, cap history size to 30
            const updatedHistory = [newItem, ...scannedHistory].slice(0, 30);
            saveHistory(updatedHistory);
            
            // Keep the scanning running or pause?
            // Let's stop scanning immediately so user can examine result without triggering multiple times
            stopCameraScanning();

            // Automatically open URL if detected
            if (isUrl(decodedText)) {
              try {
                window.open(getCleanUrl(decodedText), '_blank');
              } catch (e) {
                console.warn("Popup block prevented auto-opening scanned link:", e);
              }
            }
          },
          (errorMessage) => {
            // Scanning in progress logs, redundant to print verbose errors
          }
        )
        .then(() => {
          setIsScanning(true);
        })
        .catch((err) => {
          console.error("Start scanning failed", err);
          setCameraError(`Failed to bind camera: ${err.message || err}`);
          setIsScanning(false);
        });
      } catch (e) {
        console.error("Scanner exception", e);
        setCameraError("Exception booting camera stream.");
      }
    });
  };

  // Stop the live video camera scanner
  const stopCameraScanning = () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      return html5QrCodeRef.current.stop()
        .then(() => {
          html5QrCodeRef.current = null;
          setIsScanning(false);
        })
        .catch((err) => {
          console.error("Stop scanning failed", err);
        });
    }
    setIsScanning(false);
    return Promise.resolve();
  };

  // Handle uploaded local image file
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!window.Html5Qrcode) {
      setFileError("Scanner library is still loading.");
      return;
    }

    setFileError(null);
    setScannedText(null);
    setUploadedFileName(file.name);

    // Create a temporary element to scan on
    const html5QrCode = new window.Html5Qrcode("dummy-hidden-scanner");
    
    html5QrCode.scanFile(file, true)
      .then((decodedText) => {
        triggerSuccessBeep();
        setScannedText(decodedText);
        setShowResultModal(true);
        
        const newItem = {
          text: decodedText,
          timestamp: new Date().toLocaleString(),
          id: Date.now()
        };
        saveHistory([newItem, ...scannedHistory].slice(0, 30));
        
        // Clean up
        html5QrCode.clear();

        // Automatically open URL if detected
        if (isUrl(decodedText)) {
          try {
            window.open(getCleanUrl(decodedText), '_blank');
          } catch (e) {
            console.warn("Popup block prevented auto-opening scanned link:", e);
          }
        }
      })
      .catch((err) => {
        console.error("File scanning error:", err);
        setFileError("Failed to detect QR code in this image. Ensure the code is in clear focus and has adequate lighting!");
      });
  };

  // Copy result to clipboard
  const copyToClipboard = async () => {
    if (!scannedText) return;
    try {
      await navigator.clipboard.writeText(scannedText);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error(err);
    }
  };

  // Detect URL
  const isUrl = (text) => {
    if (!text) return false;
    const clean = text.trim();
    return clean.startsWith('http://') || clean.startsWith('https://') || clean.startsWith('www.');
  };

  const getCleanUrl = (text) => {
    const clean = text.trim();
    if (clean.startsWith('www.')) {
      return `https://${clean}`;
    }
    return clean;
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      
      {/* Hidden container for file upload decoding */}
      <div id="dummy-hidden-scanner" style={{ display: 'none' }}></div>

      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>QR Code Scanner</span>
      </div>


      {/* Main Workspace Layout */}
      <div style={{
        maxWidth: '720px',
        margin: '0 auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>
          
          {/* Module Selector Tabs */}
          <div style={{
            display: 'flex',
            background: 'rgba(15, 23, 42, 0.04)',
            padding: '4px',
            borderRadius: '12px',
            gap: '4px',
            overflowX: 'auto',
            whiteSpace: 'nowrap'
          }}>
            <button
              onClick={() => setActiveTab('camera')}
              style={{
                flex: 1,
                background: activeTab === 'camera' ? '#ffffff' : 'transparent',
                color: activeTab === 'camera' ? 'var(--primary)' : 'var(--text-secondary)',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.85rem',
                padding: '12px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'var(--transition-smooth)',
                boxShadow: activeTab === 'camera' ? 'var(--shadow-sm)' : 'none'
              }}
              className="tab-pill"
            >
              <Camera size={16} /> Live Video Camera
            </button>
            <button
              onClick={() => setActiveTab('file')}
              style={{
                flex: 1,
                background: activeTab === 'file' ? '#ffffff' : 'transparent',
                color: activeTab === 'file' ? 'var(--primary)' : 'var(--text-secondary)',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.85rem',
                padding: '12px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'var(--transition-smooth)',
                boxShadow: activeTab === 'file' ? 'var(--shadow-sm)' : 'none'
              }}
              className="tab-pill"
            >
              <Upload size={16} /> Upload QR Image File
            </button>
          </div>

          {/* Core Camera Pane */}
          {activeTab === 'camera' && (
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: 'var(--shadow-sm)'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Camera size={18} style={{ color: 'var(--primary)' }} /> Camera Stream Config
              </h2>
              <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', margin: '0 0 20px 0' }}>
                Select your capture device below, grant microphone/camera system permission, and start live streaming to detect matrix codes instantly!
              </p>

              {cameraError && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  background: 'rgba(239, 68, 68, 0.04)',
                  border: '1px solid rgba(239, 68, 68, 0.12)',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  marginBottom: '20px'
                }}>
                  <AlertCircle size={16} style={{ color: '#ef4444', flexShrink: 0, marginTop: '2px' }} />
                  <p style={{ fontSize: '0.78rem', color: '#ef4444', margin: 0, lineHeight: 1.45 }}>{cameraError}</p>
                </div>
              )}

              {/* Selector & Beep Row */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                marginBottom: '24px',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                {/* Camera device selector */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '220px' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Select Camera Device</label>
                  <select
                    value={activeCameraId}
                    onChange={(e) => setActiveCameraId(e.target.value)}
                    disabled={isScanning}
                    style={{
                      height: '42px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      background: '#ffffff',
                      color: 'var(--text-primary)',
                      padding: '0 10px',
                      fontSize: '0.85rem',
                      outline: 'none',
                      cursor: isScanning ? 'not-allowed' : 'pointer',
                      transition: 'var(--transition-smooth)'
                    }}
                  >
                    {camerasList.map((cam) => (
                      <option key={cam.id} value={cam.id}>{cam.label || `Webcam ${camerasList.indexOf(cam) + 1}`}</option>
                    ))}
                  </select>
                </div>

                {/* Beep sound switcher */}
                <button
                  onClick={() => setBeepEnabled(!beepEnabled)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    height: '42px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: beepEnabled ? 'rgba(5, 117, 251, 0.05)' : '#ffffff',
                    color: beepEnabled ? 'var(--primary)' : 'var(--text-secondary)',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    padding: '0 16px',
                    cursor: 'pointer',
                    marginTop: '20px',
                    transition: 'var(--transition-smooth)'
                  }}
                >
                  {beepEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                  Beep Sound: {beepEnabled ? 'ON' : 'OFF'}
                </button>
              </div>

              {/* Live Video Feed Wrapper Frame */}
              <div style={{
                position: 'relative',
                background: '#0f172a',
                borderRadius: '16px',
                overflow: 'hidden',
                aspectRatio: '4/3',
                width: '100%',
                maxWidth: '640px',
                margin: '0 auto 24px auto',
                boxShadow: 'var(--shadow-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* HTML5 Video target element */}
                <div id="scanner-video-pane" style={{ width: '100%', height: '100%', objectFit: 'cover' }}></div>

                {/* Overlay Scanning Guide Grid (Only active while scanning) */}
                {isScanning && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 3
                  }}>
                    {/* Dark scrim overlay */}
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(15, 23, 42, 0.4)'
                    }} />

                    {/* Laser Target Box Overlay */}
                    <div style={{
                      position: 'relative',
                      width: '240px',
                      height: '240px',
                      border: '2px dashed #10b981',
                      borderRadius: '16px',
                      background: 'transparent',
                      boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.4), inset 0 0 30px rgba(16, 185, 129, 0.15)',
                      zIndex: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {/* Laser sweeping beam animation line */}
                      <div style={{
                        position: 'absolute',
                        width: '90%',
                        height: '2.5px',
                        background: '#10b981',
                        boxShadow: '0 0 10px 1px #10b981',
                        borderRadius: '50%',
                        top: '10%',
                        animation: 'sweep-animate 2.2s ease-in-out infinite'
                      }} />
                      
                      {/* Interactive Corner Angles */}
                      <div style={{ ...cornerAngleStyle, top: '-2px', left: '-2px', borderTop: '4px solid #10b981', borderLeft: '4px solid #10b981' }} />
                      <div style={{ ...cornerAngleStyle, top: '-2px', right: '-2px', borderTop: '4px solid #10b981', borderRight: '4px solid #10b981' }} />
                      <div style={{ ...cornerAngleStyle, bottom: '-2px', left: '-2px', borderBottom: '4px solid #10b981', borderLeft: '4px solid #10b981' }} />
                      <div style={{ ...cornerAngleStyle, bottom: '-2px', right: '-2px', borderBottom: '4px solid #10b981', borderRight: '4px solid #10b981' }} />
                    </div>
                  </div>
                )}

                {/* Cover view when camera is inactive */}
                {!isScanning && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    color: 'rgba(255, 255, 255, 0.6)',
                    background: '#0f172a',
                    zIndex: 2,
                    padding: '24px',
                    textAlign: 'center'
                  }}>
                    <QrCode size={48} style={{ opacity: 0.5, color: '#0ea5e9' }} />
                    <p style={{ fontSize: '0.9rem', fontWeight: 600, margin: 0 }}>Camera Stream Inactive</p>
                    <p style={{ fontSize: '0.78rem', opacity: 0.8, maxWidth: '280px', margin: 0 }}>Click the "Start Camera Scanner" trigger below to launch the video scan frame.</p>
                  </div>
                )}
              </div>

              {/* Action Toggle buttons */}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {!isScanning ? (
                  <button
                    onClick={startCameraScanning}
                    style={{
                      background: 'var(--primary)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px 28px',
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 14px -2px rgba(5, 117, 251, 0.3)',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="scan-trigger-btn"
                  >
                    <Play size={18} fill="#ffffff" /> Start Camera Scanner
                  </button>
                ) : (
                  <button
                    onClick={stopCameraScanning}
                    style={{
                      background: '#ef4444',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px 28px',
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 14px -2px rgba(239, 68, 68, 0.3)',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="scan-stop-btn"
                  >
                    <Square size={16} fill="#ffffff" /> Stop Camera Scanner
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Image File Scanner Panel */}
          {activeTab === 'file' && (
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: 'var(--shadow-sm)'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Upload size={18} style={{ color: 'var(--secondary)' }} /> Static File Decoder
              </h2>
              <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', margin: '0 0 20px 0' }}>
                Drag and drop or upload any screenshot, image file, or camera snap from your gallery. We scan the image file directly in your browser.
              </p>

              {fileError && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  background: 'rgba(239, 68, 68, 0.04)',
                  border: '1px solid rgba(239, 68, 68, 0.12)',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  marginBottom: '20px'
                }}>
                  <AlertCircle size={16} style={{ color: '#ef4444', flexShrink: 0, marginTop: '2px' }} />
                  <p style={{ fontSize: '0.78rem', color: '#ef4444', margin: 0, lineHeight: 1.45 }}>{fileError}</p>
                </div>
              )}

              {/* Upload Drag Box Container */}
              <div 
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--border-color)',
                  background: 'rgba(15, 23, 42, 0.01)',
                  borderRadius: '16px',
                  padding: '48px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'var(--transition-smooth)',
                  marginBottom: '24px'
                }}
                className="drag-upload-box"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <div style={{
                  background: 'rgba(14, 165, 233, 0.06)',
                  color: 'var(--secondary)',
                  width: '54px',
                  height: '54px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto'
                }}>
                  <Upload size={24} />
                </div>
                
                {uploadedFileName ? (
                  <div>
                    <p style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px 0' }}>Selected: {uploadedFileName}</p>
                    <p style={{ fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600, margin: 0 }}>Click again to choose another image</p>
                  </div>
                ) : (
                  <div>
                    <p style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px 0' }}>Click to Browse Image Files</p>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0 }}>Supports JPG, PNG, WEBP, and raw screenshots</p>
                  </div>
                )}
              </div>
            </div>
          )}
      </div>

      {/* Premium Glassmorphic Scan Result Modal Overlay */}
      {showResultModal && scannedText && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
          animation: 'fade-in 0.25s ease-out'
        }}>
          <div style={{
            background: '#ffffff',
            border: '1px solid var(--border-color)',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '440px',
            padding: '28px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
            position: 'relative',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            animation: 'slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
          }}>
            {/* Close Cross icon */}
            <button
              onClick={() => { setShowResultModal(false); setUploadedFileName(''); }}
              style={{
                position: 'absolute',
                top: '18px',
                right: '18px',
                background: 'rgba(15, 23, 42, 0.04)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                transition: 'var(--transition-smooth)'
              }}
              className="modal-close-cross"
            >
              <X size={16} />
            </button>

            {/* Success Icon */}
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              color: '#10b981',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              <Check size={28} strokeWidth={3} />
            </div>

            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 6px 0' }}>
                QR Code Scanned!
              </h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
                {isUrl(scannedText) 
                  ? "We detected a URL link and attempted to open it automatically in a new tab!"
                  : "We parsed the following plain text/content successfully:"}
              </p>
            </div>

            {/* Scanned Text container */}
            <div style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'left',
              maxHeight: '150px',
              overflowY: 'auto'
            }}>
              <p style={{
                margin: 0,
                fontSize: '0.88rem',
                color: 'var(--text-primary)',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5
              }}>
                {scannedText}
              </p>
            </div>

            {/* Action buttons list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={copyToClipboard}
                style={{
                  background: isCopied ? '#059669' : 'var(--primary)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'var(--transition-smooth)'
                }}
                className="action-btn"
              >
                {isCopied ? <Check size={16} /> : <Copy size={16} />}
                {isCopied ? 'Copied Successfully!' : 'Copy parsed result'}
              </button>

              {isUrl(scannedText) && (
                <a
                  href={getCleanUrl(scannedText)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: '#ffffff',
                    color: 'var(--secondary)',
                    border: '1px solid var(--secondary)',
                    borderRadius: '10px',
                    padding: '12px',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    textDecoration: 'none',
                    transition: 'var(--transition-smooth)'
                  }}
                  className="open-link-btn"
                >
                  <ExternalLink size={16} /> Open URL link manually
                </a>
              )}
              
              <button
                onClick={() => { setShowResultModal(false); setUploadedFileName(''); }}
                style={{
                  background: 'rgba(15, 23, 42, 0.04)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'var(--transition-smooth)'
                }}
                className="scan-dismiss-btn"
              >
                Scan Another QR Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styled custom components animation injects */}
      <style>{`
        @media (min-width: 992px) {
          .scanner-main-grid {
            grid-template-columns: 1.3fr 1fr !important;
          }
        }

        .tab-pill {
          transition: transform 0.15s, box-shadow 0.15s, color 0.15s !important;
        }
        .tab-pill:hover {
          color: var(--primary) !important;
        }

        .drag-upload-box:hover {
          border-color: var(--secondary) !important;
          background: rgba(14, 165, 233, 0.02) !important;
        }

        .scan-trigger-btn:hover {
          background: var(--primary-hover) !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px -2px rgba(5, 117, 251, 0.45) !important;
        }
        .scan-stop-btn:hover {
          background: #dc2626 !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px -2px rgba(239, 68, 68, 0.45) !important;
        }

        .action-btn:hover {
          background: var(--primary-hover) !important;
          transform: translateY(-1px);
        }
        .open-link-btn:hover {
          background: rgba(14, 165, 233, 0.04) !important;
          transform: translateY(-1px);
        }

        .history-log-item:hover {
          border-color: var(--secondary) !important;
          background: rgba(14, 165, 233, 0.02) !important;
        }

        @keyframes sweep-animate {
          0% { top: 10%; opacity: 0.3; }
          50% { top: 88%; opacity: 1; }
          100% { top: 10%; opacity: 0.3; }
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slide-up {
          from { transform: translateY(24px) scale(0.96); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }

        /* Video element control fixes to ensure camera fits nicely */
        #scanner-video-pane video {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }
      `}</style>

      {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="qr-scanner" />
    </div>
  );
};

const cornerAngleStyle = {
  position: 'absolute',
  width: '20px',
  height: '20px',
  pointerEvents: 'none'
};

export default QRScanner;
